import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";
import { 
  Plus, 
  MessageSquare, 
  Trash2, 
  LogOut, 
  MoreHorizontal,
  Star,
  Edit2,
  Check,
  X,
  Loader2,
  FolderOpen,
  ChevronUp,
  ChevronDown,
  PanelLeftClose,
  Settings,
  Sparkles,
  Zap,
  CreditCard,
  Shield,
  FolderPlus
} from "lucide-react";
import SettingsModal from "./SettingsModal";
import UpgradePlanModal from "./UpgradePlanModal";
import PaymentHistoryModal from "./PaymentHistoryModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";

const API = "/api";

// Individual Conversation Item Component
const ConversationItem = ({
  conversation,
  isActive,
  onClick,
  onDelete,
  onStar,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);

  const handleRename = () => {
    if (editTitle.trim() && editTitle !== conversation.title) {
      onRename(conversation.conversation_id, editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(conversation.title);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <div
      className={`
        group relative flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer
        transition-all duration-150
        ${isActive ? "bg-[#2A2A2A]" : "hover:bg-[#1E1E1E]"}
      `}
      onClick={() => !isEditing && onClick(conversation)}
      data-testid={`conversation-item-${conversation.conversation_id}`}
    >
      {/* Title or Edit Input */}
      {isEditing ? (
        <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 h-7 bg-[#1E1E1E] border-[#14A800] text-sm text-white focus:ring-1 focus:ring-[#14A800]"
            autoFocus
            maxLength={50}
          />
          <button onClick={handleRename} className="p-1 hover:bg-[#333] rounded">
            <Check className="w-4 h-4 text-[#14A800]" />
          </button>
          <button onClick={handleCancel} className="p-1 hover:bg-[#333] rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      ) : (
        <>
          {/* Star Icon (if starred) */}
          {conversation.starred && (
            <Star className="w-3 h-3 flex-shrink-0 fill-yellow-400 text-yellow-400" />
          )}
          
          <span className="flex-1 min-w-0 max-w-[140px] text-sm text-[#E8E8E8] truncate font-normal">
            {conversation.title}
          </span>

          {/* Three-dot Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:bg-[#333] rounded transition-all"
                data-testid={`conversation-menu-${conversation.conversation_id}`}
              >
                <MoreHorizontal className="w-4 h-4 text-[#888]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 bg-[#1E1E1E] border-[#333]">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onStar(conversation.conversation_id);
                }}
                className="cursor-pointer text-[#E8E8E8] focus:bg-[#2A2A2A] focus:text-white"
              >
                <Star className={`w-4 h-4 mr-2 ${conversation.starred ? "fill-yellow-400 text-yellow-400" : ""}`} />
                {conversation.starred ? "Unstar" : "Star"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="cursor-pointer text-[#E8E8E8] focus:bg-[#2A2A2A] focus:text-white"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#333]" />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(conversation);
                }}
                className="cursor-pointer text-red-400 focus:bg-red-400/10 focus:text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
};

export default function Sidebar({
  conversations,
  currentConversation,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
  onLogout,
  user,
  loading,
  onStarConversation,
  onRenameConversation,
  onToggleSidebar,
  sidebarOpen,
  onUserUpdate,
  conversationUsage,
  currentProject,
  onSelectProject,
  onNewProjectChat,
  refreshConversations,
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const navigate = useNavigate();

  // Projects state
  const [projects, setProjects] = useState([]);
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectName, setEditProjectName] = useState("");

  // Check if user is admin
  const isAdmin = user?.is_admin === true;

  // Check if user is on free plan
  const isFreePlan = !user?.plan || user?.plan === "Free Plan" || user?.plan?.toLowerCase() === "free";
  const isPro = user?.plan?.toLowerCase()?.startsWith("pro");

  // Fetch projects for Pro users
  useEffect(() => {
    if (isPro) {
      fetchProjects();
    }
  }, [isPro]);

  // Refresh projects when conversations change (to update counts)
  useEffect(() => {
    if (isPro && conversations.length >= 0) {
      // Debounce the refresh
      const timer = setTimeout(() => {
        fetchProjects();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [conversations.length, isPro]);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ name: newProjectName.trim() })
      });
      
      if (response.ok) {
        const newProject = await response.json();
        setProjects(prev => [newProject, ...prev]);
        setNewProjectName("");
        setCreateProjectOpen(false);
        toast.success("Project created successfully");
        // Auto-select the new project
        onSelectProject?.(newProject);
      } else {
        toast.error("Failed to create project");
      }
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const handleRenameProject = async (projectId) => {
    if (!editProjectName.trim()) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ name: editProjectName.trim() })
      });
      
      if (response.ok) {
        setProjects(prev => prev.map(p => 
          p.project_id === projectId ? { ...p, name: editProjectName.trim() } : p
        ));
        setEditingProject(null);
        toast.success("Project renamed");
      }
    } catch (error) {
      toast.error("Failed to rename project");
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/projects/${projectToDelete.project_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setProjects(prev => prev.filter(p => p.project_id !== projectToDelete.project_id));
        if (currentProject?.project_id === projectToDelete.project_id) {
          onSelectProject?.(null);
        }
        toast.success("Project deleted");
        refreshConversations?.();
      }
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setDeleteProjectDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  // Use conversation usage from API (tracks actual created count, not just active)
  // API returns: remaining_conversations = base_remaining + extra_conversations
  // So we need to calculate base monthly remaining separately
  const extraConversations = conversationUsage?.extra_conversations ?? 0;
  const totalRemaining = conversationUsage?.remaining_conversations ?? 3;
  const monthlyRemaining = Math.max(0, totalRemaining - extraConversations);
  const isLowOnConversations = totalRemaining <= 1;
  const isOutOfConversations = totalRemaining === 0;

  const handleDeleteClick = (conv) => {
    setConversationToDelete(conv);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (conversationToDelete) {
      onDeleteConversation(conversationToDelete.conversation_id);
    }
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  // Navigate to admin panel with token
  const handleAdminPanelClick = async () => {
    const API = "/api";
    const token = localStorage.getItem("auth_token");
    
    try {
      const response = await fetch(`${API}/auth/admin-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store admin token and navigate
        localStorage.setItem("admin_token", data.admin_token);
        localStorage.setItem("admin_email", data.admin_email);
        navigate("/admin");
      } else {
        console.error("Failed to get admin token:", response.status);
      }
    } catch (error) {
      console.error("Failed to get admin token:", error);
    }
  };

  // Open Stripe billing portal for subscription management
  const handleManageSubscription = async () => {
    setBillingLoading(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const response = await fetch(`${API}/payments/billing-portal`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe billing portal
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Unable to open billing portal");
      }
    } catch (error) {
      console.error("Failed to open billing portal:", error);
      toast.error("Failed to open billing portal");
    } finally {
      setBillingLoading(false);
    }
  };

  // Sort conversations: starred first, then by date
  const sortedConversations = [...conversations].sort((a, b) => {
    if (a.starred && !b.starred) return -1;
    if (!a.starred && b.starred) return 1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  return (
    <>
      <div className="w-64 h-screen bg-[#0D0D0D] flex flex-col overflow-hidden" data-testid="sidebar">
        {/* Header with Logo and Collapse Button */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Unizar" className="h-8 w-auto" />
          </div>
          <button
            onClick={onToggleSidebar}
            className="p-1.5 rounded-lg text-[#888] hover:text-white hover:bg-[#1E1E1E] transition-colors"
            data-testid="collapse-sidebar-btn"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="px-3 py-2">
          <button
            onClick={onNewChat}
            className="flex items-center gap-3 w-full px-3 py-2.5 text-[#E8E8E8] hover:bg-[#1E1E1E] rounded-lg transition-colors"
            data-testid="new-chat-btn"
          >
            <div className="w-6 h-6 rounded-full bg-[#2A2A2A] flex items-center justify-center">
              <Plus className="w-4 h-4" />
            </div>
            <span className="font-medium">New chat</span>
          </button>
          
          {/* Conversation Counter for Free Plan */}
          {isFreePlan && (
            <div className="px-3 mt-2" data-testid="conversation-counter">
              {/* Monthly conversations */}
              <p className={`text-xs ${
                isOutOfConversations 
                  ? "text-red-400" 
                  : isLowOnConversations 
                    ? "text-yellow-400" 
                    : "text-[#888]"
              }`}>
                {monthlyRemaining} free conversation{monthlyRemaining !== 1 ? "s" : ""} left
              </p>
              
              {/* Plus credits (if any) */}
              {extraConversations > 0 && (
                <div className="flex items-center gap-1.5 mt-1" data-testid="plus-credits">
                  <Sparkles className="w-3 h-3 text-[#14A800]" />
                  <p className="text-xs text-[#14A800]">
                    +{extraConversations} Plus credit{extraConversations !== 1 ? "s" : ""}
                  </p>
                </div>
              )}
              
              {isOutOfConversations && (
                <button
                  onClick={() => setUpgradeOpen(true)}
                  className="text-xs text-[#14A800] hover:underline mt-1"
                  data-testid="upgrade-from-counter"
                >
                  Upgrade for unlimited →
                </button>
              )}
              {isLowOnConversations && !isOutOfConversations && (
                <button
                  onClick={() => setUpgradeOpen(true)}
                  className="text-xs text-[#14A800] hover:underline mt-1"
                  data-testid="upgrade-from-counter"
                >
                  Upgrade to Pro →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <div className="px-3 py-1">
          <button 
            onClick={() => {
              onSelectProject?.(null);
              navigate("/chat");
            }}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-colors ${
              !currentProject ? "bg-[#1E1E1E] text-white" : "text-[#E8E8E8] hover:bg-[#1E1E1E]"
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            <span>Chats</span>
          </button>
        </div>

        {/* Projects Section (Pro users only) */}
        {isPro && (
          <div className="px-3 py-1">
            <div 
              className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-[#1E1E1E] rounded-lg"
              onClick={() => setProjectsExpanded(!projectsExpanded)}
            >
              <div className="flex items-center gap-3 text-[#E8E8E8]">
                <FolderOpen className="w-5 h-5" />
                <span>Projects</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreateProjectOpen(true);
                  }}
                  className="p-1 rounded hover:bg-[#2A2A2A] text-[#888] hover:text-white"
                  data-testid="create-project-btn"
                >
                  <FolderPlus className="w-4 h-4" />
                </button>
                {projectsExpanded ? (
                  <ChevronUp className="w-4 h-4 text-[#888]" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[#888]" />
                )}
              </div>
            </div>
            
            {projectsExpanded && (
              <div className="mt-1 space-y-0.5">
                {projectsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-[#888]" />
                  </div>
                ) : projects.length === 0 ? (
                  <div className="px-3 py-4 text-center">
                    <p className="text-xs text-[#666]">No projects yet</p>
                    <button
                      onClick={() => setCreateProjectOpen(true)}
                      className="text-xs text-[#14A800] hover:underline mt-1"
                    >
                      Create your first project
                    </button>
                  </div>
                ) : (
                  projects.map((project) => (
                    <div
                      key={project.project_id}
                      className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        currentProject?.project_id === project.project_id
                          ? "bg-[#1E1E1E] text-white"
                          : "text-[#E8E8E8] hover:bg-[#1E1E1E]"
                      }`}
                      onClick={() => onSelectProject?.(project)}
                    >
                      {editingProject === project.project_id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editProjectName}
                            onChange={(e) => setEditProjectName(e.target.value)}
                            className="h-7 text-sm bg-[#0a0a0a] border-[#333]"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameProject(project.project_id);
                              if (e.key === "Escape") setEditingProject(null);
                            }}
                            autoFocus
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameProject(project.project_id);
                            }}
                            className="p-1 text-[#14A800] hover:bg-[#14A800]/20 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(null);
                            }}
                            className="p-1 text-[#888] hover:bg-[#333] rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 min-w-0">
                            <FolderOpen className="w-4 h-4 text-[#14A800] shrink-0" />
                            <span className="text-sm truncate">{project.name}</span>
                            <span className="text-xs text-[#666]">({project.conversation_count})</span>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[#2A2A2A] text-[#888] hover:text-white"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#1E1E1E] border-[#333]">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNewProjectChat?.(project);
                                }}
                                className="text-[#E8E8E8] focus:text-white focus:bg-[#2A2A2A] cursor-pointer"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                New Chat
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingProject(project.project_id);
                                  setEditProjectName(project.name);
                                }}
                                className="text-[#E8E8E8] focus:text-white focus:bg-[#2A2A2A] cursor-pointer"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#333]" />
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProjectToDelete(project);
                                  setDeleteProjectDialogOpen(true);
                                }}
                                className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Recents/Chats Section */}
        <div className="flex-1 flex flex-col min-h-0 mt-2">
          <div className="px-4 py-2">
            <span className="text-xs text-[#888] font-medium">
              {currentProject ? `${currentProject.name} Chats` : "Recents"}
            </span>
          </div>
          
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#888]" />
              </div>
            ) : sortedConversations.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#666]">
                <p className="text-sm">
                  {currentProject ? "No chats in this project" : "No conversations yet"}
                </p>
                {currentProject && (
                  <button
                    onClick={() => onNewProjectChat?.(currentProject)}
                    className="text-xs text-[#14A800] hover:underline mt-2"
                  >
                    Start a chat in this project
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-0.5 px-3">
                {sortedConversations.map((conv) => (
                  <ConversationItem
                    key={conv.conversation_id}
                    conversation={conv}
                    isActive={currentConversation?.conversation_id === conv.conversation_id}
                    onClick={onSelectConversation}
                    onDelete={handleDeleteClick}
                    onStar={onStarConversation}
                    onRename={onRenameConversation}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* User Profile */}
        <div className="p-3 border-t border-[#1E1E1E]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#1E1E1E] transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="w-9 h-9 rounded-full bg-[#333] flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-white">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </span>
                  )}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-[#888] truncate">
                    {user?.plan || "Free Plan"}
                  </p>
                </div>
                <ChevronUp className="w-4 h-4 text-[#888]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              side="top"
              className="w-56 bg-[#1E1E1E] border-[#333]"
            >
              <DropdownMenuItem
                onClick={() => setSettingsOpen(true)}
                className="text-[#E8E8E8] focus:text-white focus:bg-[#2A2A2A] cursor-pointer"
                data-testid="settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPaymentHistoryOpen(true)}
                className="text-[#E8E8E8] focus:text-white focus:bg-[#2A2A2A] cursor-pointer"
                data-testid="payment-history-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Payment History
              </DropdownMenuItem>
              {isPro && (
                <DropdownMenuItem
                  onClick={handleManageSubscription}
                  disabled={billingLoading}
                  className="text-[#E8E8E8] focus:text-white focus:bg-[#2A2A2A] cursor-pointer"
                  data-testid="manage-subscription-btn"
                >
                  {billingLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem
                  onClick={handleAdminPanelClick}
                  className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer"
                  data-testid="admin-panel-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              {isFreePlan && (
                <DropdownMenuItem
                  onClick={() => setUpgradeOpen(true)}
                  className="text-[#14A800] focus:text-[#14A800] focus:bg-[#14A800]/10 cursor-pointer"
                  data-testid="upgrade-plan-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-[#333]" />
              <DropdownMenuItem
                onClick={onLogout}
                className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1E1E1E] border-[#333]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888]">
              This will permanently delete "{conversationToDelete?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#2A2A2A] border-[#333] text-white hover:bg-[#333]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Project Dialog */}
      <Dialog open={createProjectOpen} onOpenChange={setCreateProjectOpen}>
        <DialogContent className="bg-[#1E1E1E] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription className="text-[#888]">
              Organize related conversations under a project for better context.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
              className="bg-[#0a0a0a] border-[#333] text-white"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCreateProjectOpen(false)}
              className="text-[#888] hover:text-white hover:bg-[#333]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!newProjectName.trim()}
              className="bg-[#14A800] hover:bg-[#108A00] text-white"
            >
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation */}
      <AlertDialog open={deleteProjectDialogOpen} onOpenChange={setDeleteProjectDialogOpen}>
        <AlertDialogContent className="bg-[#1E1E1E] border-[#333]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888]">
              This will permanently delete "{projectToDelete?.name}" and all {projectToDelete?.conversation_count || 0} conversations in it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#2A2A2A] border-[#333] text-white hover:bg-[#333]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        user={user}
        onUserUpdate={onUserUpdate}
      />

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        user={user}
        onUserUpdate={onUserUpdate}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        open={paymentHistoryOpen}
        onOpenChange={setPaymentHistoryOpen}
      />
    </>
  );
}
